// Single source of truth for marketing-page SEO metadata. Consumed by the
// client <SeoHelmet>, the server SSR head (seoHead.ts), and the sitemap
// (blog/operations.ts). FR text — primary audience. Titles <=65 / desc <=160.
import { JOURNAL_DE_TRADING_FAQ } from "./data/journalDeTradingFaq";
import { NINJATRADER_TRADEMARK_TEXT } from "../client/components/legal/ninjaTraderTrademarkText";
export type MarketingLdKind = "software" | "webpage";

export interface SeoBodySection {
  h2: string;
  text: string;
}
export interface SeoBodyLink {
  href: string;
  label: string;
}
export interface SeoBodyFaqItem {
  question: string;
  answer: string;
}
export interface SeoBody {
  /** Page H1. */
  h1: string;
  /** 45-60 words; primary keyword within the first 20 words. */
  intro: string;
  sections: SeoBodySection[];
  internalLinks: SeoBodyLink[];
  /** Optional FAQ block, rendered in the SSR body and as FAQPage JSON-LD. */
  faq?: SeoBodyFaqItem[];
}

export interface MarketingMetaEntry {
  title: string;
  description: string;
  kind: MarketingLdKind;
  seoBody?: SeoBody;
}

export const MARKETING_META: Record<string, MarketingMetaEntry> = {
  // ── Features ──
  "/journal-de-trading": {
    title: "Journal de trading en français : gratuit & complet | Edgely",
    description:
      "Le journal de trading en français : import Tradovate et CSV, win rate, profit factor, sessions, routines et Prop Firm Manager. Commencez gratuitement.",
    kind: "software",
    seoBody: {
      h1: "Journal de trading : suivez, analysez et améliorez chaque trade",
      intro:
        "Le journal de trading Edgely vous aide à progresser à chaque séance. Suivez chaque trade, mesurez votre win rate et votre profit factor, et identifiez l'edge qui fait votre rentabilité. Entièrement en français et gratuit pour démarrer, il réunit journal, analytics, sessions, routines et gestion prop firm dans une seule application pensée pour les traders futures.",
      sections: [
        {
          h2: "Un journal de trading complet, pensé pour progresser",
          text: "Edgely réunit tout ce dont un trader a besoin pour comprendre sa performance : journalisation de chaque trade avec setup, résultat, notes et screenshots ; analytics avancés (win rate, profit factor, ratio risque/récompense, drawdown, courbe d'équité, performance par setup, session et instrument) ; sessions minutées pour préparer et débriefer chaque séance ; objectifs de progression ; Prop Firm Manager multi-comptes ; et un mode mentor pour les coachs.",
        },
        {
          h2: "Journalisation de chaque trade",
          text: "Chaque trade est enregistré avec son setup, son résultat, vos notes et vos captures d'écran. Vous gardez une trace fidèle de votre raisonnement au moment de l'exécution, ce qui vous permet ensuite de distinguer vos vraies compétences de vos coups de chance et d'isoler vos erreurs récurrentes.",
        },
        {
          h2: "Analytics avancés",
          text: "Win rate, profit factor, ratio risque/récompense, drawdown et courbe d'équité sont calculés automatiquement à partir de vos trades. Filtrez par compte, groupe, stratégie ou période, et découvrez vos meilleures plages horaires, vos meilleurs instruments et les setups qui font réellement votre rentabilité.",
        },
        {
          h2: "Sessions et routines",
          text: "Préparez chaque séance avec une routine avant marché, chronométrez votre session, évaluez votre humeur, puis débriefez à froid. En reliant votre état mental à vos résultats, vous transformez la discipline en avantage mesurable plutôt qu'en bonne résolution.",
        },
        {
          h2: "Objectifs de progression",
          text: "Fixez des objectifs concrets (PnL, win rate, nombre de trades) avec échéance et récurrence, et suivez votre avancement période après période. Vos objectifs deviennent un fil conducteur qui garde votre trading orienté vers le progrès plutôt que vers le résultat d'une seule séance.",
        },
        {
          h2: "Prop Firm Manager",
          text: "Gérez tous vos comptes prop firm au même endroit : statuts, règles de drawdown par firme, alertes, payouts et ROI global. Edgely connaît les spécificités d'Apex, TopStep, Tradeify, Lucid, FundedNext, Bulenox, MyFundedFutures et TakeProfitTrader, avec un calendrier et des analytics de payouts dédiés.",
        },
        {
          h2: "Mode mentor",
          text: "Les coachs accèdent au journal, aux sessions et aux statistiques de leurs élèves selon les permissions accordées, écrivent des notes privées et suivent la progression de chaque trader dans un cadre sécurisé. Un même compte Edgely sert ainsi le trader et son mentor.",
        },
        {
          h2: "Importez vos trades en quelques secondes",
          text: "La voie privilégiée est la synchronisation automatique de vos trades Tradovate, disponible sur le plan Max (en bêta) : vos exécutions arrivent seules dans votre journal. Restent aussi le copier-coller depuis Tradovate, l'import CSV (Tradovate ou TopStepX) et la saisie manuelle. La synchronisation MetaTrader (MT4 et MT5) arrive bientôt. Vos trades sont prêts à être analysés, sans formule ni nettoyage manuel.",
        },
        {
          h2: "Pourquoi tenir un journal de trading ?",
          text: "Tenir un journal sert d'abord à identifier votre edge : quels setups, quels instruments et quelles plages horaires vous rapportent vraiment. Il sert ensuite à corriger vos erreurs récurrentes, celles qui se répètent séance après séance sans que vous en ayez conscience. Il sert enfin à gérer votre psychologie, en reliant vos décisions à votre état mental pour reprendre le contrôle sur vos biais.",
        },
        {
          h2: "Excel, papier ou application ?",
          text: "Le papier ne calcule rien et se perd. Excel calcule, mais au prix d'une saisie fastidieuse et de formules qui cassent dès que votre historique grossit. Une application comme Edgely importe vos trades automatiquement, calcule vos métriques sans erreur, gère vos règles de drawdown prop firm et reste fiable dans le temps. Vous passez du temps à trader, pas à maintenir un tableur.",
        },
        {
          h2: "Combien coûte un journal de trading ?",
          text: "Edgely propose un plan gratuit (un compte, trades illimités, analytics de base, sans carte bancaire), un plan Pro à 15€ par mois (comptes illimités, Prop Firm Manager, analytics avancés) et un plan Max à 29€ par mois (synchronisation automatique de vos trades Tradovate). En comparaison, Tradezella démarre à 29$ par mois sans plan gratuit. Notre comparatif Edgely contre Tradezella détaille les différences.",
        },
      ],
      faq: JOURNAL_DE_TRADING_FAQ,
      internalLinks: [
        { href: "/tarifs", label: "Voir les tarifs" },
        { href: "/vs/tradezella", label: "Edgely vs Tradezella" },
        { href: "/fonctionnalites/prop-firm-manager", label: "Le Prop Firm Manager" },
      ],
    },
  },
  "/fonctionnalites/prop-firm-manager": {
    title: "Prop Firm Manager | Gérez tous vos comptes — Edgely",
    description:
      "Apex, TopStep, Lucid, FundedNext. Statuts, payouts, ROI global. Tout en un dashboard.",
    kind: "software",
    seoBody: {
      h1: "Prop Firm Manager : gérez tous vos comptes prop firm",
      intro:
        "Le Prop Firm Manager d'Edgely réunit tous vos comptes Apex, TopStep, Lucid et FundedNext dans un seul dashboard. Suivez les statuts, les payouts, les frais et le ROI global de chaque compte, avec un calendrier dédié et des analytics de payouts. Importez votre historique par CSV et pilotez votre activité prop firm sans tableur.",
      sections: [
        { h2: "Tous vos comptes dans un dashboard", text: "Statuts challenge, funded et payouts centralisés pour chaque prop firm." },
        { h2: "Analytics de payouts et ROI global", text: "Mesurez la rentabilité réelle de votre activité prop firm, frais inclus." },
      ],
      internalLinks: [
        { href: "/tarifs", label: "Voir les tarifs" },
        { href: "/journal-de-trading", label: "Le journal de trading Edgely" },
      ],
    },
  },
  "/fonctionnalites/analytics-trading": {
    title: "Analytics de Trading Avancées | Edgely",
    description:
      "Win Rate, Profit Factor, Sharpe Ratio, meilleures plages horaires. Vos données parlent.",
    kind: "software",
    seoBody: {
      h1: "Analytics de trading avancées pour prop traders",
      intro:
        "Les analytics Edgely transforment vos trades en décisions. Win rate, profit factor, Sharpe ratio, meilleures plages horaires et performance par symbole : vos données révèlent votre edge réel. Filtrez par compte, groupe, stratégie ou période et identifiez ce qui fonctionne vraiment dans votre trading futures.",
      sections: [
        { h2: "Les métriques qui comptent", text: "Win rate, profit factor, ratio risque/récompense et drawdown calculés automatiquement." },
        { h2: "Trouvez vos meilleures plages horaires", text: "Analyse par heure, par jour et par symbole pour concentrer votre énergie là où vous gagnez." },
      ],
      internalLinks: [
        { href: "/journal-de-trading", label: "Le journal de trading Edgely" },
        { href: "/tarifs", label: "Voir les tarifs" },
      ],
    },
  },
  "/fonctionnalites/sessions-de-trading": {
    title: "Sessions de Trading | Review et Analyse — Edgely",
    description:
      "Chronomètre ta session, évalue ton humeur, identifie tes patterns comportementaux.",
    kind: "software",
    seoBody: {
      h1: "Sessions de trading : review et analyse comportementale",
      intro:
        "Les sessions de trading Edgely vous aident à travailler votre discipline. Chronométrez votre session, évaluez votre humeur, ajoutez vos notes et votre vidéo, puis identifiez vos patterns comportementaux. Chaque session est analysée pour relier votre état mental à vos résultats et corriger vos biais récurrents.",
      sections: [
        { h2: "Chronomètre et évaluation d'humeur", text: "Mesurez la durée de vos sessions et votre état mental pour relier discipline et performance." },
        { h2: "Patterns comportementaux", text: "Identifiez les biais qui plombent vos résultats et suivez vos progrès dans le temps." },
      ],
      internalLinks: [
        { href: "/fonctionnalites/analytics-trading", label: "Analytics de trading" },
        { href: "/tarifs", label: "Voir les tarifs" },
      ],
    },
  },
  "/fonctionnalites/mode-mentor": {
    title: "Mode Mentor Trading | Suivez vos élèves — Edgely",
    description:
      "Dashboard complet de vos élèves traders. Sessions, PNL, statistiques, vidéos annotées.",
    kind: "software",
    seoBody: {
      h1: "Mode mentor : suivez vos élèves traders",
      intro:
        "Le mode mentor d'Edgely permet aux coachs de suivre leurs élèves depuis leur propre dashboard. Accédez au journal, aux sessions, aux statistiques et aux vidéos annotées de chaque élève selon les permissions accordées. Écrivez des notes privées et accompagnez la progression de vos traders dans un cadre sécurisé et structuré.",
      sections: [
        { h2: "Le dashboard de vos élèves", text: "Journal, sessions, PNL et statistiques de chaque élève accessibles selon ses permissions." },
        { h2: "Notes privées et accompagnement", text: "Annotez les sessions et gardez un suivi structuré de la progression de chaque trader." },
      ],
      internalLinks: [
        { href: "/solutions/coach-trading", label: "Edgely pour les coachs" },
        { href: "/tarifs", label: "Voir les tarifs" },
      ],
    },
  },
  "/fonctionnalites/dashboard-trading": {
    title: "Dashboard Trading Modulaire | Edgely",
    description:
      "Créez vos pages, ajoutez vos widgets, filtrez par prop firm. Votre dashboard, vos règles.",
    kind: "software",
    seoBody: {
      h1: "Dashboard de trading modulaire et personnalisable",
      intro:
        "Le dashboard Edgely s'adapte à votre façon de trader. Créez vos pages, ajoutez vos widgets, filtrez par prop firm et organisez votre vue comme vous le souhaitez. Net PnL, win rate, calendrier, courbe d'équité et widgets prop firm : composez le tableau de bord qui correspond à vos priorités.",
      sections: [
        { h2: "Vos widgets, vos règles", text: "Ajoutez, déplacez et redimensionnez des widgets pour construire la vue qui vous convient." },
        { h2: "Pages personnalisées et filtres", text: "Créez plusieurs pages et filtrez par compte, groupe, stratégie ou prop firm." },
      ],
      internalLinks: [
        { href: "/fonctionnalites/analytics-trading", label: "Analytics de trading" },
        { href: "/tarifs", label: "Voir les tarifs" },
      ],
    },
  },
  "/integrations": {
    title: "Intégrations | Edgely",
    description:
      "Connecte tes plateformes à Edgely : sync auto NinjaTrader et Tradovate (plan Max), import CSV Tradovate et TopstepX, sync MetaTrader bientôt.",
    kind: "software",
    seoBody: {
      h1: "Intégrations : connectez vos plateformes à Edgely",
      intro:
        "Edgely se connecte à vos plateformes de trading pour centraliser vos données. Synchronisez automatiquement vos trades NinjaTrader et Tradovate (plan Max) ou importez vos historiques par CSV depuis Tradovate et TopstepX. Plus de saisie manuelle : vos trades arrivent dans votre journal prêts à être analysés.",
      sections: [
        // NinjaTrader Vendor Program : la version SSR (bots) doit refléter la
        // page SPA — NinjaTrader cité comme plateforme supportée + trademark
        // notice (verbatim, importée) sur toute page qui le mentionne.
        { h2: "Sync automatique NinjaTrader & Tradovate", text: "Connectez votre compte NinjaTrader ou Tradovate et synchronisez vos trades automatiquement, en lecture seule (plan Max, en bêta)." },
        { h2: "Sync MetaTrader (bientôt)", text: "La synchronisation automatique des comptes MetaTrader 4 et 5 arrive prochainement sur le plan Max." },
        { h2: "Import CSV Tradovate et TopstepX", text: "Importez vos historiques de trades en quelques clics, sans formules ni copier-coller." },
        { h2: "Trademark Notice", text: NINJATRADER_TRADEMARK_TEXT },
      ],
      internalLinks: [
        { href: "/journal-de-trading", label: "Le journal de trading Edgely" },
        { href: "/tarifs", label: "Voir les tarifs" },
      ],
    },
  },
  // ── Solutions ──
  "/solutions/prop-trader-debutant": {
    title: "Edgely pour les Prop Traders Débutants",
    description:
      "Tu commences les prop firms ? Identifie tes erreurs dès le premier trade. Plan gratuit, sans CB.",
    kind: "webpage",
    seoBody: {
      h1: "Edgely pour les prop traders débutants",
      intro:
        "Vous débutez les prop firms ? Edgely vous aide à identifier vos erreurs dès le premier trade. Le plan gratuit, sans carte bancaire, vous permet de journaliser vos trades et de comprendre vos patterns avant d'investir dans une évaluation. Apprenez à respecter les règles de drawdown et progressez méthodiquement vers le funded.",
      sections: [
        { h2: "Démarrez gratuitement, sans carte bancaire", text: "Journalisez vos trades et analysez vos premières erreurs sans engagement." },
        { h2: "Comprenez les règles prop firm", text: "Suivez votre drawdown et apprenez à respecter les règles de votre évaluation." },
      ],
      internalLinks: [
        { href: "/journal-de-trading", label: "Le journal de trading Edgely" },
        { href: "/tarifs", label: "Voir les tarifs" },
      ],
    },
  },
  "/solutions/prop-trader-multi-comptes": {
    title: "Edgely pour les Traders Multi-Comptes",
    description:
      "Apex, TopStep, Lucid en simultané ? Centralisez comptes, payouts et performances en un endroit.",
    kind: "webpage",
    seoBody: {
      h1: "Edgely pour les traders multi-comptes",
      intro:
        "Vous gérez Apex, TopStep et Lucid en simultané ? Edgely centralise tous vos comptes prop firm en un seul endroit. Suivez payouts, frais et performances par compte ou en agrégé, avec des groupes de comptes pour analyser votre activité globale. Fini les tableurs éparpillés : votre activité multi-comptes devient lisible.",
      sections: [
        { h2: "Tous vos comptes centralisés", text: "Apex, TopStep, Lucid et FundedNext suivis ensemble, avec statuts et payouts." },
        { h2: "Analytics agrégées par groupe", text: "Regroupez vos comptes pour mesurer la performance réelle de votre activité prop firm." },
      ],
      internalLinks: [
        { href: "/fonctionnalites/prop-firm-manager", label: "Le Prop Firm Manager" },
        { href: "/tarifs", label: "Voir les tarifs" },
      ],
    },
  },
  "/solutions/coach-trading": {
    title: "Edgely pour les Coachs Trading | Mode Mentor",
    description:
      "Suivez vos élèves depuis leur dashboard Edgely. Sessions, PNL, statistiques, vidéos annotées.",
    kind: "webpage",
    seoBody: {
      h1: "Edgely pour les coachs trading : mode mentor",
      intro:
        "Vous coachez des traders ? Le mode mentor d'Edgely vous donne accès au dashboard de vos élèves : sessions, PNL, statistiques et vidéos annotées. Suivez leur progression, écrivez des notes privées et accompagnez chaque élève selon les permissions qu'il vous accorde, dans un cadre clair et sécurisé.",
      sections: [
        { h2: "Le dashboard de vos élèves", text: "Accédez au journal, aux sessions et aux statistiques de chaque élève selon ses permissions." },
        { h2: "Un suivi structuré", text: "Notes privées et vue d'ensemble pour accompagner la progression de vos traders." },
      ],
      internalLinks: [
        { href: "/fonctionnalites/mode-mentor", label: "Le mode mentor" },
        { href: "/tarifs", label: "Voir les tarifs" },
      ],
    },
  },
  // ── Comparaisons ──
  "/vs/tradezella": {
    title: "Edgely vs TradeZella 2026 | Comparatif",
    description:
      "Edgely ou TradeZella ? Comparatif complet pour les prop traders : prix, fonctionnalités, interface française.",
    kind: "webpage",
    seoBody: {
      h1: "Edgely vs TradeZella : quel journal de trading choisir ?",
      intro:
        "Vous hésitez entre Edgely et TradeZella pour suivre vos trades prop firm ? Découvrez la comparaison complète : fonctionnalités, prix, compatibilité prop firm et support MetaTrader. Edgely est pensé pour les prop traders futures francophones, avec une interface en français et un Prop Firm Manager intégré que TradeZella ne propose pas.",
      sections: [
        { h2: "Fonctionnalités : Edgely vs TradeZella", text: "Journal, analytics, sessions, routines, mode mentor et Prop Firm Manager dans une seule application." },
        { h2: "Prix et rapport qualité/prix", text: "Edgely propose un plan gratuit, un plan Pro à 15€ par mois et un plan Max à 29€ par mois avec sync automatique." },
        { h2: "Pourquoi les prop traders choisissent Edgely", text: "Gestion du drawdown EOD, support MNQ/NQ/ES et suivi multi-comptes prop firm en natif." },
      ],
      internalLinks: [
        { href: "/tarifs", label: "Voir les tarifs" },
        { href: "/meilleur-journal-de-trading", label: "Meilleur journal de trading" },
        { href: "/alternatives-journal-de-trading", label: "Alternatives à TradeZella" },
      ],
    },
  },
  "/vs/tradersync": {
    title: "Edgely vs TraderSync 2026 | Comparatif",
    description:
      "Edgely ou TraderSync ? Comparatif honnête pour les prop traders francophones.",
    kind: "webpage",
    seoBody: {
      h1: "Edgely vs TraderSync : comparatif pour prop traders",
      intro:
        "Edgely ou TraderSync pour suivre vos trades prop firm ? Voici un comparatif honnête pour les prop traders francophones : fonctionnalités, prix et compatibilité prop firm. Edgely se distingue par son interface en français, sa gestion du drawdown EOD et son Prop Firm Manager intégré, pensés pour les futures traders.",
      sections: [
        { h2: "Fonctionnalités comparées", text: "Journal, analytics, sessions et Prop Firm Manager face aux fonctionnalités de TraderSync." },
        { h2: "Prix et interface française", text: "Plan gratuit, Pro à 15€ par mois, et une interface entièrement en français." },
      ],
      internalLinks: [
        { href: "/meilleur-journal-de-trading", label: "Meilleur journal de trading" },
        { href: "/tarifs", label: "Voir les tarifs" },
      ],
    },
  },
  "/vs/notion": {
    title: "Edgely vs Notion pour le Trading 2026",
    description:
      "Pourquoi un template Notion ne suffit plus pour suivre tes prop firms. Comparatif complet.",
    kind: "webpage",
    seoBody: {
      h1: "Edgely vs Notion pour le trading",
      intro:
        "Un template Notion suffit-il pour suivre vos prop firms ? Au-delà de quelques trades, Notion montre ses limites : pas d'analytics automatiques, pas de calcul de drawdown, pas d'import CSV. Edgely calcule vos métriques automatiquement et gère vos comptes prop firm en natif, là où Notion vous oblige à tout construire à la main.",
      sections: [
        { h2: "Analytics automatiques vs formules manuelles", text: "Edgely calcule win rate, profit factor et drawdown sans que vous écriviez une seule formule." },
        { h2: "Pensé pour les prop firms", text: "Suivi multi-comptes, payouts et règles de drawdown impossibles à reproduire dans Notion." },
      ],
      internalLinks: [
        { href: "/meilleur-journal-de-trading", label: "Meilleur journal de trading" },
        { href: "/tarifs", label: "Voir les tarifs" },
      ],
    },
  },
  "/vs/excel": {
    title: "Edgely vs Excel pour le Trading 2026",
    description:
      "Arrête de tenir ton journal de trading sur Excel. Analytics automatiques, import CSV, zéro formules.",
    kind: "webpage",
    seoBody: {
      h1: "Edgely vs Excel pour le trading",
      intro:
        "Tenez-vous encore votre journal de trading sur Excel ? Les formules cassent, l'import est fastidieux et les analytics restent limitées. Edgely automatise tout : import CSV depuis Tradovate et TopstepX, analytics calculées automatiquement et zéro formule à maintenir. Concentrez-vous sur votre trading, pas sur votre tableur.",
      sections: [
        { h2: "Zéro formule à maintenir", text: "Win rate, profit factor et drawdown calculés automatiquement à chaque trade importé." },
        { h2: "Import CSV en quelques clics", text: "Importez vos historiques Tradovate et TopstepX sans copier-coller ni nettoyage manuel." },
      ],
      internalLinks: [
        { href: "/meilleur-journal-de-trading", label: "Meilleur journal de trading" },
        { href: "/tarifs", label: "Voir les tarifs" },
      ],
    },
  },
  "/meilleur-journal-de-trading": {
    title: "Meilleur Journal de Trading 2026 | Comparatif",
    description:
      "Quel journal de trading choisir en 2026 ? Comparatif Edgely, TradeZella, TraderSync, Notion, Excel.",
    kind: "webpage",
    seoBody: {
      h1: "Meilleur journal de trading pour prop traders en 2026",
      intro:
        "Cherchez-vous le meilleur journal de trading pour prop firm ? Edgely centralise vos trades, calcule votre drawdown en temps réel et vous alerte avant de violer les règles de votre prop firm. Conçu pour les futures traders sur MNQ, NQ et ES, il combine journal, analytics avancées et gestion multi-comptes dans une seule application.",
      sections: [
        { h2: "Pourquoi Edgely est le journal de trading le plus complet", text: "Journal, analytics, sessions minutées, routines, objectifs et Prop Firm Manager réunis au même endroit." },
        { h2: "Comparatif des meilleurs journaux de trading", text: "Edgely, TradeZella, TraderSync, Notion et Excel comparés pour les prop traders francophones." },
      ],
      internalLinks: [
        { href: "/vs/tradezella", label: "Edgely vs TradeZella" },
        { href: "/tarifs", label: "Voir les tarifs" },
        { href: "/journal-de-trading", label: "Le journal de trading Edgely" },
      ],
    },
  },
  "/alternatives-journal-de-trading": {
    title: "Meilleures Alternatives à TradeZella 2026",
    description:
      "Cherchez-vous une alternative à TradeZella ? Les meilleures options pour les prop traders en 2026.",
    kind: "webpage",
    seoBody: {
      h1: "Meilleures alternatives à TradeZella en 2026",
      intro:
        "Vous cherchez une alternative à TradeZella ou TraderSync ? Edgely est le journal de trading prop firm le plus adapté aux futures traders : support MNQ/NQ/ES, gestion du drawdown EOD et Prop Firm Manager intégré. Découvrez pourquoi les prop traders francophones migrent vers une solution pensée pour leurs règles spécifiques.",
      sections: [
        { h2: "Top alternatives TradeZella comparées", text: "Un panorama des solutions de journal de trading et de leurs limites pour les prop traders futures." },
        { h2: "Edgely : la meilleure alternative pour les prop traders futures", text: "Interface française, drawdown en temps réel et suivi multi-comptes prop firm en natif." },
      ],
      internalLinks: [
        { href: "/vs/tradezella", label: "Edgely vs TradeZella" },
        { href: "/tarifs", label: "Voir les tarifs" },
      ],
    },
  },
  // ── Pricing ──
  "/tarifs": {
    title: "Tarifs Edgely | Free, Pro 15€/mois, Pro Annuel",
    description:
      "Plan gratuit sans CB, Pro à 15€/mois, Pro Annuel à 144€/an. Choisissez votre plan Edgely.",
    kind: "webpage",
    seoBody: {
      h1: "Tarifs Edgely : journal de trading prop firm",
      intro:
        "Edgely propose un plan gratuit et des plans payants adaptés aux prop traders sérieux. Accédez aux fonctionnalités avancées dès 15€ par mois, sans carte bancaire pour démarrer. Chaque plan inclut le journal de trades, et les plans payants débloquent analytics avancées, comptes illimités et sync automatique.",
      sections: [
        { h2: "Plan Free : gratuit pour démarrer", text: "Journal de trades illimité, un compte et analytics de base, sans carte bancaire." },
        { h2: "Plan Pro : 15€ par mois ou 144€ par an", text: "Comptes illimités, analytics avancées, sessions, routines et objectifs illimités." },
        { h2: "Plan Max : 29€ par mois avec sync auto", text: "Tout le plan Pro plus la synchronisation automatique de vos trades Tradovate (en bêta), la sync MetaTrader arrivant bientôt." },
      ],
      internalLinks: [
        { href: "/journal-de-trading", label: "Le journal de trading Edgely" },
      ],
    },
  },
  "/comparateur": {
    title: "Comparateur de prop firms 2026 | Edgely",
    description:
      "Comparez les meilleures prop firms futures : prix, drawdown, plateformes et commissions. Trouvez le compte de trading adapté à votre style.",
    kind: "webpage",
    seoBody: {
      h1: "Comparateur de prop firms futures",
      intro:
        "Comparez en un coup d'œil les offres des meilleures prop firms de trading futures : frais d'évaluation, type de drawdown, plateformes supportées et commissions round-trip. Filtrez par taille de compte, plateforme ou type de challenge pour trouver la firme adaptée à votre style de trading.",
      sections: [
        { h2: "Comparez les prix et les frais d'activation", text: "Prix d'évaluation, remises en cours, frais d'activation et coût total réel pour chaque offre de prop firm." },
        { h2: "Drawdown, objectifs et plateformes", text: "Type de drawdown (EOD, EOT, trailing, statique), objectif de profit et plateformes supportées comparés côte à côte." },
        { h2: "Commissions round-trip", text: "Commissions micro et mini par aller-retour pour estimer le coût réel de votre activité de trading." },
      ],
      internalLinks: [
        { href: "/tarifs", label: "Les tarifs Edgely" },
        { href: "/journal-de-trading", label: "Le journal de trading Edgely" },
      ],
    },
  },
};

export const MARKETING_PATHS = Object.keys(MARKETING_META);
